// src/modules/permisos/components/DashboardCalendarioWidget.jsx
// (renombrado desde PermisosCalendarioWidget.jsx: ya no es solo permisos)
// Calendario que junta varias fuentes de eventos: permisos/vacaciones
// (Aceptado, Espera y Emitido — para que se vean venir los pendientes de
// aprobar tambien) de todo el equipo de la sucursal (no solo los propios —
// la idea es que se sepa quien va a estar disponible o no) + coordinaciones
// de requerimientos de la sucursal. Sumar una fuente nueva mas adelante es
// agregar otro fetch + mapear a la misma forma de evento, sin tocar el resto.
//
// Tres formas de usar este archivo:
//   - <DashboardCalendarioWidget fullPage /> (/permisos/calendario): el
//     FullCalendar completo con vistas mes/semana/dia.
//   - <DashboardCalendarioWidget /> (fullPage=false): mini-calendario tipo
//     "pildoras" + panel de detalle, todo junto — para usar en un solo lugar.
//   - <CalendarioEquipoProvider> + <CalendarioMiniGrid> + <CalendarioDetalleDia>
//     por separado: mismo mini-calendario y panel de detalle, pero en dos
//     columnas independientes del dashboard (comparten estado via contexto,
//     para que el clic en un dia del grid actualice el panel de al lado
//     aunque esten en Cards distintas).
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import {
  Box, CircularProgress, Alert, Dialog, DialogContent, DialogActions, Button,
  Typography, Stack, Paper, IconButton, Divider, FormControl, InputLabel, Select, MenuItem, Avatar,
} from "@mui/material";
import { useTheme, alpha, darken, lighten } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import axios from "../../../config/axiosConfig";
import { obtenerCoordinaciones } from "../../calendario/services/calendarioService";

dayjs.locale("es");

const ESTADO_PERMISO_COLOR = (theme) => ({
  Espera: theme.palette.warning.main,
  Emitido: theme.palette.grey[500],
  Aceptado: theme.palette.success.main,
  Rechazado: theme.palette.error.main,
});

// Colores por estado de coordinacion — mismo mapeo que ya usa el calendario
// de Coordinaciones (modules/calendario/components/CalendarioFull.jsx).
const ESTADO_COORDINACION_COLOR = {
  "0": "#9e9e9e",
  "2": "#ffc107",
  "3": "#03a9f4",
  "7": "#dd8a7d",
  "8": "#dad3d2",
};

// Nombres de estado — mismo mapeo que ya usa RequerimientoHeader.jsx. Un
// estado sin nombre conocido cae al numero crudo como antes.
const ESTADO_COORDINACION_LABEL = {
  "0": "Eliminado",
  "1": "Coordinación",
  "2": "Programación",
  "3": "Trabajo de campo",
  "4": "Trabajo de campo",
  "7": "Revisión en Sistemas",
  "8": "Finalizado",
};
const nombreEstadoCoordinacion = (estado) =>
  ESTADO_COORDINACION_LABEL[String(estado)] || `Estado ${estado ?? "—"}`;

// Misma lista que ya usa el selector de CalendarioFull.jsx (coordinaciones).
// "La Paz(El Alto)" (sucursal real de algunos usuarios, ver
// utils/noticiasHelpers.js SUCURSALES_NOTICIA) no tiene su propia opcion
// aca porque el backend ya la trata como la misma sucursal que "La Paz"
// (sucursalesEquivalentes) — se normaliza a "La Paz" para que el selector
// siempre tenga un valor valido.
const SUCURSALES_DISPONIBLES = ["La Paz", "Cochabamba", "Santa Cruz"];
const normalizarSucursalUI = (sucursal) => (sucursal === "La Paz(El Alto)" ? "La Paz" : sucursal);

// Selector de sucursal — por defecto el calendario muestra la del usuario
// logueado, pero cualquiera puede cambiarla para ver la disponibilidad de
// otra sucursal (igual que ya se puede en el calendario de Coordinaciones).
function SelectorSucursal({ sucursal, onChange }) {
  return (
    <FormControl variant="standard" size="small" sx={{ width: 260, mb: 1 }}>
      <InputLabel id="calendario-equipo-sucursal-label" sx={{ fontSize: "0.75rem" }}>Sucursal</InputLabel>
      <Select
        labelId="calendario-equipo-sucursal-label"
        value={sucursal}
        onChange={(e) => onChange(e.target.value)}
        sx={{ fontSize: "0.85rem" }}
      >
        {SUCURSALES_DISPONIBLES.map((s) => (
          <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// FullCalendar trata "end" como exclusivo en eventos de dia completo — hay
// que sumarle un dia a fecha_fin para que el ultimo dia se vea pintado.
const diaSiguiente = (fechaISO) => {
  const d = new Date(fechaISO);
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

// Tono (color del tema) de cada estado para las etiquetas del detalle. Los colores del FullCalendar
// (ESTADO_COORDINACION_COLOR) siguen igual; acá se usan tonos del tema para que el texto se lea en claro y oscuro.
const TONO_COORDINACION = { "0": "grey", "1": "warning", "2": "warning", "3": "info", "4": "info", "7": "secondary", "8": "success" };
const TONO_PERMISO = { Espera: "warning", Emitido: "grey", Aceptado: "success", Rechazado: "error" };

// Etiqueta de estado: fondo suave, texto del mismo tono (oscurecido en claro, aclarado en oscuro) y un punto de color.
function EstadoBadge({ tono = "grey", children }) {
  return (
    <Box
      component="span"
      sx={(theme) => {
        const c = tono === "grey" ? theme.palette.grey[500] : theme.palette[tono].main;
        return {
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1,
          py: 0.25,
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1.6,
          whiteSpace: "nowrap",
          flexShrink: 0,
          bgcolor: alpha(c, theme.palette.mode === "dark" ? 0.22 : 0.14),
          color: theme.palette.mode === "dark" ? lighten(c, 0.35) : darken(c, 0.35),
        };
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor" }} />
      {children}
    </Box>
  );
}

const iniciales = (nombre) =>
  String(nombre || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("");

// Contenido de un item (permiso o coordinacion) — compartido entre el panel
// de detalle del mini-calendario y el dialog del FullCalendar (fullPage).
function DetalleItem({ item, navigate }) {
  if (item.tipo === "permiso") {
    const dias = Number(item.dias) || 0;
    return (
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar sx={{ width: 40, height: 40, fontSize: "0.85rem", fontWeight: 700, bgcolor: "action.selected", color: "text.primary" }}>
          {iniciales(item.nombreEmpleado)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.35 }}>{item.razon || "Permiso"}</Typography>
            <EstadoBadge tono={TONO_PERMISO[item.estado] || "grey"}>{item.estado}</EstadoBadge>
          </Stack>
          <Typography variant="body2" color="text.secondary" noWrap>{item.nombreEmpleado}</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, color: "text.secondary" }}>
            <EventOutlinedIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">
              {formatFecha(item.fecha_ini)} al {formatFecha(item.fecha_fin)}
              {dias ? ` · ${dias} día${dias === 1 ? "" : "s"}` : ""}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    );
  }

  const hora = item.hora?.slice(0, 5);
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      {/* Hora, como en una agenda */}
      <Box
        sx={{
          width: 56,
          flexShrink: 0,
          py: 0.75,
          borderRadius: 1.5,
          textAlign: "center",
          bgcolor: "action.hover",
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>
          {hora || "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, letterSpacing: "0.04em" }}>
          HORA
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.35 }}>
            {item.razon_social || "Coordinación"}
          </Typography>
          <EstadoBadge tono={TONO_COORDINACION[String(item.estado)] || "info"}>
            {nombreEstadoCoordinacion(item.estado)}
          </EstadoBadge>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5, color: "text.secondary" }}>
          {item.cod && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption">Req. #{item.cod}</Typography>
            </Stack>
          )}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PersonOutlineIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={item.tecnico ? { color: "text.primary", fontWeight: 600 } : { fontStyle: "italic" }}>
              {item.tecnico || "Sin técnico asignado"}
            </Typography>
          </Stack>
        </Stack>

        {item.cod && (
          <Button
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: "16px !important" }} />}
            onClick={() => navigate(`/requerimiento/${item.cod}`)}
            sx={{ mt: 0.5, ml: -0.75, px: 0.75, textTransform: "none", fontWeight: 700 }}
          >
            Ver requerimiento
          </Button>
        )}
      </Box>
    </Stack>
  );
}

const DIAS_SEMANA = ["L", "M", "X", "J", "V", "S", "D"];

// Mini-calendario tipo "pildoras": dias con eventos resaltados, el
// seleccionado en color solido, hoy con un borde — parecido a los
// calendarios compactos de apps de citas/reservas.
function MiniMonthGrid({ mes, onChangeMes, eventosPorDia, selectedKey, onSelectDay }) {
  const theme = useTheme();
  const inicioMes = mes.startOf("month");
  const finMes = mes.endOf("month");
  const offset = (inicioMes.day() + 6) % 7; // 0 = lunes
  const totalDias = finMes.date();
  const hoyKey = dayjs().format("YYYY-MM-DD");

  const celdas = [];
  for (let i = 0; i < offset; i++) celdas.push(null);
  for (let d = 1; d <= totalDias; d++) celdas.push(d);

  return (
    <Box sx={{ width: 260, flexShrink: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <IconButton size="small" onClick={() => onChangeMes(mes.subtract(1, "month"))}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" sx={{ fontWeight: 700, textTransform: "capitalize" }}>
          {mes.format("MMMM YYYY")}
        </Typography>
        <IconButton size="small" onClick={() => onChangeMes(mes.add(1, "month"))}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, mb: 0.5 }}>
        {DIAS_SEMANA.map((d) => (
          <Typography key={d} variant="caption" align="center" color="text.secondary" sx={{ fontWeight: 700 }}>
            {d}
          </Typography>
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
        {celdas.map((d, idx) => {
          if (d == null) return <Box key={`vacio-${idx}`} />;
          const key = mes.date(d).format("YYYY-MM-DD");
          const tieneEventos = (eventosPorDia.get(key)?.length || 0) > 0;
          const esHoy = key === hoyKey;
          const esSeleccionado = key === selectedKey;
          return (
            <Box
              key={key}
              onClick={() => onSelectDay(key)}
              sx={{
                aspectRatio: "1 / 1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "10px",
                cursor: "pointer",
                fontSize: "0.78rem",
                fontWeight: esSeleccionado ? 800 : 600,
                bgcolor: esSeleccionado
                  ? theme.palette.primary.main
                  : tieneEventos
                    ? alpha(theme.palette.primary.main, 0.18)
                    : "transparent",
                color: esSeleccionado ? "#fff" : "text.primary",
                border: `1.5px solid ${esHoy && !esSeleccionado ? theme.palette.primary.main : "transparent"}`,
                transition: "background-color 120ms ease",
                "&:hover": {
                  bgcolor: esSeleccionado ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.28),
                },
              }}
            >
              {d}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// Columna de al lado del mini-calendario: el detalle del dia seleccionado —
// reemplaza al popup que abria antes al hacer clic.
function PanelDetalleDia({ selectedKey, eventosPorDia, navigate }) {
  // Permisos primero (quién no está ese día) y después las coordinaciones por hora.
  const items = useMemo(
    () =>
      (eventosPorDia.get(selectedKey) || []).slice().sort((a, b) => {
        if (a.tipo !== b.tipo) return a.tipo === "permiso" ? -1 : 1;
        return String(a.hora || "").localeCompare(String(b.hora || ""));
      }),
    [eventosPorDia, selectedKey]
  );
  const fechaTexto = selectedKey ? dayjs(selectedKey).format("dddd D [de] MMMM") : "";
  const fechaLabel = fechaTexto.charAt(0).toUpperCase() + fechaTexto.slice(1);

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", height: "100%" }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          {fechaLabel || "Elige un día"}
        </Typography>
        {items.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {items.length} evento{items.length === 1 ? "" : "s"}
          </Typography>
        )}
      </Stack>
      {items.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center", color: "text.secondary", border: 1, borderStyle: "dashed", borderColor: "divider", borderRadius: 2 }}>
          <EventOutlinedIcon sx={{ fontSize: 28, opacity: 0.6 }} />
          <Typography variant="body2">Sin eventos para este día.</Typography>
        </Box>
      ) : (
        <Stack spacing={1} sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
          {items.map((item, idx) => (
            <Paper
              key={`${item.tipo}-${item.id ?? idx}`}
              variant="outlined"
              sx={{ p: 1.5, borderRadius: 2, transition: "border-color 0.15s ease", "&:hover": { borderColor: "primary.main" } }}
            >
              <DetalleItem item={item} navigate={navigate} />
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}

// Hook con todo el estado/datos del calendario — usado tanto por el widget
// "todo junto" (default export) como por el Provider para la version en
// columnas separadas, asi ambos comparten exactamente la misma logica.
//
// sucursalInicial llega de afuera (la sucursal del usuario logueado) y se
// usa solo como valor por defecto la primera vez que este disponible (puede
// tardar un tick porque el perfil se carga async); a partir de ahi el
// usuario puede cambiarla libremente con el selector, sin que se le pise.
function useCalendarioEquipoData(sucursalInicial) {
  const theme = useTheme();
  const [permisos, setPermisos] = useState([]);
  const [coordinaciones, setCoordinaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mes, setMes] = useState(() => dayjs());
  const [selectedKey, setSelectedKey] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [sucursal, setSucursal] = useState(normalizarSucursalUI(sucursalInicial) || "La Paz");
  const sucursalInicializada = useRef(Boolean(sucursalInicial));

  useEffect(() => {
    if (!sucursalInicializada.current && sucursalInicial) {
      sucursalInicializada.current = true;
      setSucursal(normalizarSucursalUI(sucursalInicial));
    }
  }, [sucursalInicial]);

  useEffect(() => {
    setLoading(true);
    setError("");
    Promise.all([
      axios.get("/api/permisos/disponibilidad-equipo", { params: { sucursal } })
        .then(({ data }) => data?.data || [])
        .catch(() => []),
      obtenerCoordinaciones(sucursal || "La Paz").catch(() => []),
    ])
      .then(([listaPermisos, listaCoordinaciones]) => {
        setPermisos(listaPermisos);
        setCoordinaciones(Array.isArray(listaCoordinaciones) ? listaCoordinaciones : []);
      })
      .catch((e) => setError(e?.response?.data?.message || "No se pudo cargar el calendario."))
      .finally(() => setLoading(false));
  }, [sucursal]);

  const coloresPermiso = ESTADO_PERMISO_COLOR(theme);

  const eventosPermisos = useMemo(() => permisos.map((s) => {
    const nombreEmpleado = [s.nombre, s.paterno].filter(Boolean).join(" ") || "Empleado";
    return {
      id: `permiso-${s.id}`,
      title: `${nombreEmpleado} - ${s.razon}`,
      start: String(s.fecha_ini).slice(0, 10),
      end: diaSiguiente(s.fecha_fin),
      allDay: true,
      backgroundColor: coloresPermiso[s.estado] || coloresPermiso.Emitido,
      borderColor: coloresPermiso[s.estado] || coloresPermiso.Emitido,
      extendedProps: { tipo: "permiso", ...s, nombreEmpleado },
    };
  }), [permisos, coloresPermiso]);

  const eventosCoordinaciones = useMemo(() => coordinaciones.map((item, i) => {
    const soloFecha = String(item.fecha || "").split("T")[0];
    const hora = item.hora || "08:00:00";
    const color = ESTADO_COORDINACION_COLOR[String(item.estado)] || item.color || "#2196f3";
    return {
      id: `coord-${item.id ?? i}`,
      title: `${item.tecnico || "Sin técnico"} - ${item.razon_social || ""}`,
      start: `${soloFecha}T${hora}`,
      backgroundColor: color,
      borderColor: color,
      extendedProps: { tipo: "coordinacion", ...item, soloFecha, hora },
    };
  }), [coordinaciones]);

  const events = useMemo(
    () => [...eventosPermisos, ...eventosCoordinaciones],
    [eventosPermisos, eventosCoordinaciones]
  );

  // Agrupa por dia (YYYY-MM-DD) para el mini-calendario: un permiso de varios
  // dias aparece resaltado en TODOS los dias que cubre, no solo el primero.
  const eventosPorDia = useMemo(() => {
    const map = new Map();
    const push = (key, item) => {
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    };
    eventosPermisos.forEach((ev) => {
      const item = ev.extendedProps;
      const cursor = new Date(`${String(item.fecha_ini).slice(0, 10)}T00:00:00`);
      const fin = new Date(`${String(item.fecha_fin).slice(0, 10)}T00:00:00`);
      while (cursor <= fin) {
        push(cursor.toISOString().slice(0, 10), item);
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    eventosCoordinaciones.forEach((ev) => push(ev.extendedProps.soloFecha, ev.extendedProps));
    return map;
  }, [eventosPermisos, eventosCoordinaciones]);

  return {
    loading, error, mes, setMes, selectedKey, setSelectedKey, sucursal, setSucursal,
    coloresPermiso, eventosPermisos, eventosCoordinaciones, events, eventosPorDia,
  };
}

const CalendarioEquipoContext = createContext(null);

// Envuelve las columnas separadas (mini-grid + detalle) para que compartan
// el mismo estado — un clic en el grid de una Card actualiza el panel de
// detalle de la Card vecina.
export function CalendarioEquipoProvider({ sucursal, children }) {
  const value = useCalendarioEquipoData(sucursal);
  return <CalendarioEquipoContext.Provider value={value}>{children}</CalendarioEquipoContext.Provider>;
}

function useCalendarioEquipoContext() {
  const ctx = useContext(CalendarioEquipoContext);
  if (!ctx) throw new Error("CalendarioMiniGrid/CalendarioDetalleDia deben usarse dentro de <CalendarioEquipoProvider>");
  return ctx;
}

// Solo el mini-calendario — para usar en su propia columna/Card.
export function CalendarioMiniGrid() {
  const {
    loading, error, mes, setMes, selectedKey, setSelectedKey, sucursal, setSucursal, eventosPorDia,
  } = useCalendarioEquipoContext();
  return (
    <Box>
      <SelectorSucursal sucursal={sucursal} onChange={setSucursal} />
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <MiniMonthGrid
            mes={mes}
            onChangeMes={setMes}
            eventosPorDia={eventosPorDia}
            selectedKey={selectedKey}
            onSelectDay={setSelectedKey}
          />
        </Box>
      )}
    </Box>
  );
}

// Solo el panel de detalle del dia seleccionado — para usar en su propia
// columna/Card, al lado de <CalendarioMiniGrid />.
export function CalendarioDetalleDia() {
  const { loading, error, selectedKey, eventosPorDia, coloresPermiso } = useCalendarioEquipoContext();
  const navigate = useNavigate();
  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} /></Box>;
  if (error) return null; // el error ya se muestra en la columna del mini-calendario
  return (
    <PanelDetalleDia
      selectedKey={selectedKey}
      eventosPorDia={eventosPorDia}
      coloresPermiso={coloresPermiso}
      navigate={navigate}
    />
  );
}

export default function DashboardCalendarioWidget({ sucursal, fullPage = false }) {
  const navigate = useNavigate();
  const [seleccionado, setSeleccionado] = useState(null); // solo fullPage (Dialog)
  const {
    loading, error, mes, setMes, selectedKey, setSelectedKey,
    sucursal: sucursalActual, setSucursal, coloresPermiso, events, eventosPorDia,
  } = useCalendarioEquipoData(sucursal);

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} /></Box>;
  }
  if (error) return <Alert severity="error">{error}</Alert>;

  if (fullPage) {
    return (
      <Box sx={{ "& .fc": { fontSize: "0.85rem" } }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="calendario-fullpage-sucursal-label">Sucursal</InputLabel>
            <Select
              labelId="calendario-fullpage-sucursal-label"
              label="Sucursal"
              value={sucursalActual}
              onChange={(e) => setSucursal(e.target.value)}
            >
              {SUCURSALES_DISPONIBLES.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {Object.entries(coloresPermiso).map(([estado, color]) => (
            <Stack key={estado} direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: color }} />
              <Typography variant="caption" color="text.secondary">{estado}</Typography>
            </Stack>
          ))}
        </Stack>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{ left: "prev,next today", center: "title", right: "dayGridMonth,timeGridWeek,timeGridDay" }}
          events={events}
          eventClick={(info) => setSeleccionado(info.event.extendedProps)}
          height={750}
          locale={esLocale}
          buttonText={{ today: "Hoy", month: "Mes", week: "Semana", day: "Día" }}
          dayMaxEvents
        />

        <Dialog open={!!seleccionado} onClose={() => setSeleccionado(null)} maxWidth="xs" fullWidth>
          {seleccionado && (
            <>
              <DialogContent dividers>
                <DetalleItem item={seleccionado} coloresPermiso={coloresPermiso} navigate={navigate} />
              </DialogContent>
              <DialogActions>
                <Button size="small" onClick={() => setSeleccionado(null)}>Cerrar</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" }, height: "100%", alignItems: "stretch" }}>
      <Box sx={{ alignSelf: "flex-start" }}>
        <SelectorSucursal sucursal={sucursalActual} onChange={setSucursal} />
        <MiniMonthGrid
          mes={mes}
          onChangeMes={setMes}
          eventosPorDia={eventosPorDia}
          selectedKey={selectedKey}
          onSelectDay={setSelectedKey}
        />
      </Box>
      <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
      <PanelDetalleDia
        selectedKey={selectedKey}
        eventosPorDia={eventosPorDia}
        coloresPermiso={coloresPermiso}
        navigate={navigate}
      />
    </Box>
  );
}
